<script setup lang="ts">
const route = useRoute('tournaments-tournament-games')
const { $api } = useNuxtApp()
const { t } = useI18n()
const localePath = useLocalePath()
const authStore = useAuthStore()

const tournamentId = parseInt(route.params.tournament as string)

definePageMeta({
  name: 'tournaments-tournament-details',
})

const { data: tournament, refresh } = await useApi('/tournaments/{tournament}', {
  path: {
    tournament: tournamentId,
  },
  key: 'tournament-detail-' + tournamentId,
  deep: true,
})

onMounted(() => {
  if (tournament.value?.closed && !tournament.value?.complete) {
    const intervalId = setInterval(async () => {
      refresh()
    }, 30000)

    onUnmounted(() => {
      clearInterval(intervalId)
    })
  }
})

const FooterLinks = computed(() => {
  const links = []

  links.push(
    {
      label: t('Info'),
      icon: 'tabler:info-circle',
      to: localePath(`/tournaments/${route.params.tournament}`),
      exact: true,
    },
  )

  if (tournament.value?.listing_only) {
    return links
  }

  if (tournament.value?.phase !== 'complete') {
    links.push(
      {
        label: t('Games'),
        icon: 'tabler:list',
        to: localePath(`/tournaments/${route.params.tournament}/games`),
        disabled: !tournament.value?.closed,
      },
    )
  }

  if (tournament.value?.with_group_phase) {
    links.push(
      {
        label: t('Groups'),
        icon: 'tabler:users',
        to: localePath(`/tournaments/${route.params.tournament}/groups`),
        disabled: !tournament.value?.closed,
      },
    )
  }

  if (tournament.value?.with_knockout_phase) {
    links.push(
      {
        label: t('Winners KO'),
        icon: 'tabler:tournament',
        to: localePath(`/tournaments/${route.params.tournament}/winner-brackets`),
        disabled: !tournament.value?.closed,
      },
    )

    if (tournament.value?.with_b_round) {
      links.push(
        {
          label: t('Consolation KO'),
          icon: 'tabler:tournament',
          to: localePath(`/tournaments/${route.params.tournament}/consolation-brackets`),
          disabled: !tournament.value?.closed,
        },
      )
    }
  }

  links.push(
    {
      label: t('Results'),
      icon: 'tabler:award',
      to: localePath(`/tournaments/${route.params.tournament}/results`),
      disabled: !tournament.value?.complete,
      tooltip: {
        text: t('Will become active when the tournament is complete'),
      },
    },
  )

  return links
})

// SEO stuff
const generateSeoDescription = () => {
  if (tournament.value?.description) {
    return tournament.value?.description
  }
  else {
    return t('Join the {name} at {event_name}. A {type} tournament for {gender} players.', {
      name: tournament.value?.name ?? '',
      event_name: tournament.value?.event_name ?? '',
      type: tournament.value?.type ?? '',
      gender: tournament.value?.gender ?? '',
    })
  }
}

if (tournament.value) {
  useSeoMeta({
    title: `${tournament.value.name} @ ${tournament.value.event_name}`,
    description: generateSeoDescription(),
  })

  defineOgImageComponent('DartsSpace', {
    siteLogo: '/light-logo.png',
    headline: new Date(tournament.value.starts_at).toDateString(),
    title: tournament.value.name,
    description: tournament.value.event_name?.slice(0, 100),
    image: tournament.value.image?.replace('dartsspace.test', 'api.dartsspace.com'),
  })
}

function adminUnpublishRanking() {
  $api('/quick-unpublish', {
    path: undefined,
    method: 'POST',
    body: {
      tournament_id: tournament.value?.id,
    },
  })
}
</script>

<template>
  <UiPage
    v-if="tournament"
    padding
    :title="tournament.name"
    :description="tournament.event_name"
    :scroll-inner="route.name?.toString()?.startsWith('tournaments-tournament-brackets___')"
  >
    <template
      v-if="authStore.shouldShowManage"
      #top-actions
    >
      <UiButton
        class="bg-destructive text-white"
        @click="adminUnpublishRanking"
      >
        {{ $t('Unpublish') }}
      </UiButton>
    </template>
    <template #top-below>
      <nav class="flex gap-2">
        <UiTabs :default-value="route.path">
          <UiTabsList
            :pill="true"
            class="relative"
          >
            <UiTabsTrigger
              v-for="(link, index) in FooterLinks.filter(link => !link.disabled)"
              :key="link.label"
              :value="index"
              :disabled="link.disabled"
              replace
              as-child
            >
              <NuxtLink
                :to="link.to"
                class="flex items-center gap-2"
              >
                <Icon :name="link.icon" />
                {{ link.label }}
              </NuxtLink>
            </UiTabsTrigger>
          </UiTabsList>
        </UiTabs>
      </nav>
    </template>
    <NuxtPage
      v-if="tournament"
      :tournament="tournament"
    />
  </UiPage>
</template>
